import { motion } from "framer-motion";
import { Factory, GraduationCap, Handshake, Landmark, Building2, UserRoundPlus, Users } from "lucide-react";
import { TeamStrength } from "@/components/people/TeamStrength";
import { roleColor, roleTotals } from "@/components/people/roles";

const Tile = ({ id, label, value, icon: Icon, i, onClick }) => (
  <motion.button
    type="button"
    onClick={onClick}
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: i * 0.05, duration: 0.3 }}
    data-testid={`people-stat-${id}`}
    className="group flex flex-col justify-between gap-3 bg-card p-4 text-left transition-colors duration-200 hover:bg-muted"
  >
    <span className="flex items-center justify-between gap-2">
      <span className="label-caps">{label}</span>
      <Icon className="h-4 w-4 text-muted-foreground transition-colors duration-200 group-hover:text-primary" />
    </span>
    <span className="font-display text-3xl font-black tabular-nums" data-testid={`people-stat-${id}-value`}>
      {value}
    </span>
  </motion.button>
);

const PortalBar = ({ stats }) => {
  const parts = [
    ["active", "Signed in", stats.active, "hsl(var(--healthy))"],
    ["invited", "Invited", stats.invited, "hsl(var(--watch))"],
    ["off", "Not on the portal", stats.off_portal, "hsl(var(--muted-foreground) / 0.45)"],
  ];
  const total = parts.reduce((s, p) => s + p[2], 0) || 1;
  return (
    <div data-testid="people-portal-bar">
      <div className="label-caps">On the portal</div>
      <div className="mt-2 flex h-3 w-full overflow-hidden bg-muted">
        {parts.map(([k, , n, c]) => (
          <motion.span
            key={k}
            initial={{ width: 0 }}
            animate={{ width: `${(n / total) * 100}%` }}
            transition={{ duration: 0.6 }}
            style={{ backgroundColor: c }}
          />
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px]">
        {parts.map(([k, label, n, c]) => (
          <span key={k} className="flex items-center gap-1.5" data-testid={`people-portal-${k}`}>
            <span className="h-2 w-2" style={{ backgroundColor: c }} />
            {label} <b className="font-data">{n}</b>
          </span>
        ))}
      </div>
    </div>
  );
};

/** A tile goes when a super-admin has switched its whole category off. */
const TILE_CATEGORIES = { management: ["management"], sdtd: ["group_lead", "desk_officer"],
  partners: ["partners"], guests: ["guests"], trained: ["trained"] };
const hiddenTile = (data, id) =>
  (TILE_CATEGORIES[id] || [null]).every((k) => k && data.hidden_categories.includes(k));

/** The team at a glance: how many, where, in which role, and who is on the portal. */
export const TeamStats = ({ data, onJump, filtered }) => {
  const { stats } = data;
  const tiles = [
    ["people", "On the teams", stats.people, Users, "people-sections"],
    ["management", "Management", stats.management, Landmark, "people-management"],
    ["sdtd", "SDTD", stats.sdtd, Building2, "people-sdtd"],
    ["unit-members", "Unit team members", stats.unit_members, Factory, "people-units"],
    ["partners", "Pravartanam Partners", stats.partners, Handshake, "people-partners"],
    ["guests", "Invited Members", stats.guests, UserRoundPlus, "people-guests"],
    ["trained", "Trained resources", stats.trained, GraduationCap, "people-units"],
  ].filter(([id]) => (id !== "guests" || data.can_edit_all) && !hiddenTile(data, id));
  const totals = roleTotals(data.units).filter((t) => !data.hidden_roles.includes(t.role));
  return (
    <section className="mt-6 border border-border" data-testid="people-dashboard">
      <div className="grid grid-cols-2 gap-px bg-border sm:grid-cols-4" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(150px, 1fr))` }}>
        {tiles.map(([id, label, value, icon, target], i) => (
          <Tile key={id} id={id} label={label} value={value} icon={icon} i={i} onClick={() => onJump(target)} />
        ))}
      </div>
      <div className="grid gap-px border-t border-border bg-border lg:grid-cols-[1fr_320px]">
        <div className="bg-card p-4">
          <TeamStrength units={data.units} filtered={filtered} hiddenRoles={data.hidden_roles} onPick={(plant) => onJump(`people-unit-${plant}`)} />
        </div>
        <div className="space-y-5 bg-card p-4">
          <PortalBar stats={stats} />
          <div data-testid="people-role-totals">
            <div className="label-caps">Unit teams by role</div>
            <ul className="mt-2 space-y-1.5">
              {totals.map((t) => (
                <li key={t.role} className="flex items-center justify-between gap-2 text-xs">
                  <span className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5" style={{ backgroundColor: roleColor(t.role) }} />
                    {t.role}
                  </span>
                  <b className="font-data" data-testid={`people-role-total-${t.role.replace(/\s+/g, "-")}`}>
                    {t.count}
                  </b>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};
